/* eslint react/require-default-props: "off" */

import { useEffect, useRef, useState } from 'react';
import { Row } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import Loading from 'components/Loading';
import CampaignList from 'components/subscribe/property/CampaignList';
import Checkbox from 'components/subscribe/property/Checkbox';
import InvalidLink from 'components/subscribe/property/InvalidLink';
import PageHeader from 'components/subscribe/property/PageHeader';
import UpdatePreferences from 'components/subscribe/property/UpdatePreferences';

import Tick from 'assets/svg/tick.svg?react';

import * as API from 'api/api';
import useInterval from 'hooks/useInterval';
import useQuery from 'hooks/useQuery';

import './ManagePreferences.css';

const JWT_SESSION_STORAGE_KEY_NAME = 'subscriberPreferencesToken';
const CAMPAIGN_SESSION_STORAGE_KEY_NAME = 'campaignURN';
const EMAIL_SESSION_STORAGE_KEY_NAME = 'email';

const isValidRedirectURL = (redirectURL?: string): boolean =>
  !!redirectURL && redirectURL !== '';

/**
 * Branding type definition
 */
type Campaign = {
  campaignURN: string;
  name: string;
};

type Branding = {
  logoImageURL?: string;
  logoImageAddress?: string;
  signupButtonLabel?: string;
  emailInputLabel?: string;
  helperText?: string;
  titleCopy?: string;
  descriptionCopy?: string;
  confirmationTitleCopy?: string;
  confirmationBodyCopy?: string;
  redirectURL?: string;
  redirectMessageCopy?: string;
  preferencesUpdatedCopy?: string;
  selectAllNewslettersButtonText?: string;
  unsubscribedFromAllCopy?: string;
  orderedCampaigns: Campaign[];
  page?: string;
  emailAddress?: string;
  propertyPrivacyPolicyEnabled?: boolean;
  propertyPrivacyPolicyLink?: string;
  propertyPrivacyPolicyText?: string;
  confirmationCancelButtonLabel?: string;
};

interface ManagePreferencesProps {
  branding: Branding | null;
  success?: boolean;
}

function ManagePreferences({
  branding,
  success = false,
}: ManagePreferencesProps) {
  // UseParams returns Record<string, string | undefined>
  const params = useParams<Record<string, string>>();
  const propertyURN = params.entityKey ?? '';
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();

  const isPreview = branding?.page ?? false;

  let email: string = query.get('email') ?? '';
  if (email) {
    sessionStorage.setItem(EMAIL_SESSION_STORAGE_KEY_NAME, email);
  } else {
    email = sessionStorage.getItem(EMAIL_SESSION_STORAGE_KEY_NAME) ?? '';
  }
  /* Set default email address if rendering a preview */
  if (isPreview) {
    email = branding?.emailAddress ?? '';
  }

  let campaignKey: string = query.get('campaignURN') ?? '';
  if (campaignKey) {
    sessionStorage.setItem(CAMPAIGN_SESSION_STORAGE_KEY_NAME, campaignKey);
  } else {
    campaignKey =
      sessionStorage.getItem(CAMPAIGN_SESSION_STORAGE_KEY_NAME) ?? '';
  }

  let token: string = query.get('token') ?? '';
  if (token) {
    /* Store the JWT for later use */
    sessionStorage.setItem(JWT_SESSION_STORAGE_KEY_NAME, token);
    /* Remove the JWT from the URL */
    history.replace(location.pathname);
  } else {
    /* Retrieve the JWT previously stored */
    token = sessionStorage.getItem(JWT_SESSION_STORAGE_KEY_NAME) ?? '';
  }

  const [isLoading, setLoading] = useState(true);
  const [isTokenValid, setTokenValid] = useState(true);

  type CampaignListItem = {
    isSelected: boolean;
    campaignURN: string;
    name: string;
  };
  const [campaignList, setCampaignList] = useState<CampaignListItem[] | null>(
    null
  );
  const originalCampaignList = useRef<CampaignListItem[] | null>(null);

  const [isCountingDown, setCountingDown] = useState(false);
  const [timeRemainingSecs, setTimeRemainingSecs] = useState(3);

  useInterval(
    // Callback - code to be executed each interval
    () => {
      if (timeRemainingSecs > 0) {
        setTimeRemainingSecs(timeRemainingSecs - 1);
      } else {
        setCountingDown(false);
        handleRedirect();
      }
    },
    // Interval - return null to stop the interval when the countdown reaches zero
    isCountingDown ? 1000 : null
  );

  const handleRedirect = () => {
    if (branding?.redirectURL) {
      window.location.href = branding.redirectURL;
    }
  };

  /* Call the relevant endpoint to get the reader's subscription data */
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  useEffect(() => {
    const fetchCampaignList = async () => {
      let initialCampaignList;
      try {
        initialCampaignList = await API.getSubscriberPreferences({
          jwt: token,
        });
        // Filter and re-order initial campaign list using ordered campaigns in branding
        if (branding && branding.orderedCampaigns) {
          const campaignURNIndexMap = new Map(
            branding.orderedCampaigns.map((campaign, index) => [
              campaign.campaignURN,
              index,
            ])
          );
          initialCampaignList = initialCampaignList
            .filter((campaign: any) => campaignURNIndexMap.has(campaign.urn))
            .sort(
              (a: any, b: any) =>
                (campaignURNIndexMap.get(a.urn) ?? 0) -
                (campaignURNIndexMap.get(b.urn) ?? 0)
            );
        }
        originalCampaignList.current = initialCampaignList.map(
          (campaign: any) => ({
            isSelected: campaign.isSubscribed,
            campaignURN: campaign.urn,
            name: campaign.name,
          })
        );
        setCampaignList(originalCampaignList.current);
      } catch {
        /* Assume the endpoint call failed because of an invalid token */
        setErrorMessage('Failed to get preferences');
        setTokenValid(false);
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      /* If a valid token exists, fetch the reader's current subscriptions */
      fetchCampaignList();
    } else if (isPreview && branding) {
      /* Just use the default list of campaigns when rendering a preview */
      originalCampaignList.current = branding.orderedCampaigns.map(
        campaign => ({
          campaignURN: campaign.campaignURN,
          isSelected: false,
          name: campaign.name,
        })
      );
      setCampaignList(originalCampaignList.current);
    }
  }, [branding, isPreview, token]);

  const changesExist = () => {
    if (!originalCampaignList.current || !campaignList) {
      return false;
    }
    const originalSelectedCampaigns = originalCampaignList.current
      .filter(campaign => campaign.isSelected)
      .map(campaign => campaign.campaignURN);
    const currentSelectedCampaigns = campaignList
      .filter(campaign => campaign.isSelected)
      .map(campaign => campaign.campaignURN);
    return (
      originalSelectedCampaigns.length !== currentSelectedCampaigns.length ||
      originalSelectedCampaigns.some(
        (campaignURN, index) => campaignURN !== currentSelectedCampaigns[index]
      )
    );
  };

  const FORM_STATES = {
    PENDING: 'PENDING',
    UPDATED: 'UPDATED',
    CANCELLED: 'CANCELLED',
  };

  const [isBusy, setBusy] = useState(false);
  const [formState, setFormState] = useState(FORM_STATES.PENDING);

  const isCancelDisabled = useRef(
    isBusy ||
      campaignList?.filter(campaign => campaign.isSelected).length === 0 ||
      isPreview
  );
  const isUpdateDisabled =
    isBusy ||
    campaignList?.filter(campaign => campaign.isSelected).length === 0 ||
    !changesExist() ||
    isPreview;

  /* Render error page if the token is missing or invalid */
  /* except if we are rendering a preview with POST-provided branding data */
  if ((!token || !isTokenValid) && !isPreview) {
    return (
      <InvalidLink
        branding={
          branding
            ? {
                logoImageAddress: branding.logoImageAddress ?? '',
                confirmationCancelButtonLabel:
                  branding.confirmationCancelButtonLabel ?? '',
              }
            : {
                logoImageAddress: '',
                confirmationCancelButtonLabel: 'Cancel',
              }
        }
        propertyURN={propertyURN}
        campaignURN={campaignKey}
        emailAddress={email}
      />
    );
  }

  /* Render nothing while waiting for branding data */
  /* or while waiting for the default campaign list for a preview to be set */
  if (!branding || !campaignList) {
    return null;
  }

  /* Render spinner while waiting for endpoint call */
  if (isLoading && !!token) {
    return <Loading />;
  }

  const handleCancel = async () => {
    setBusy(true);
    try {
      await API.postSubscriberPreferences({
        jwt: token,
        unsubscribeFromAll: true,
      });
      setFormState(FORM_STATES.CANCELLED);
      if (isValidRedirectURL(branding?.redirectURL)) {
        setCountingDown(true);
      }
    } catch {
      setErrorMessage('Failed to update preferences');
      /* Assume the endpoint call failed because of an invalid token */
      setTokenValid(false);
    } finally {
      setBusy(false);
    }
  };

  const handleSelect = (campaignURN: string) => {
    if (!campaignList) return;
    const newCampaignList = campaignList.map(campaign => {
      if (campaign.campaignURN === campaignURN) {
        const modifiedCampaign = {
          ...campaign,
          isSelected: !campaign.isSelected,
        };
        return modifiedCampaign;
      }
      return campaign;
    });
    setCampaignList(newCampaignList);
    setErrorMessage(null);
  };

  const handleAllSelect = () => {
    if (!campaignList) return;
    const shouldSelectAll = campaignList.every(
      campaign => campaign.isSelected === true
    );
    const newCampaignList = campaignList.map(campaign => ({
      ...campaign,
      isSelected: !shouldSelectAll,
    }));
    setCampaignList(newCampaignList);
    setErrorMessage(null);
  };

  const handleUpdate = async () => {
    if (!campaignList) return;
    const subscribeTo = campaignList
      .filter(campaign => campaign.isSelected)
      .map(campaign => campaign.campaignURN);
    const unsubscribeFrom = campaignList
      .filter(campaign => !campaign.isSelected)
      .map(campaign => campaign.campaignURN);

    setBusy(true);
    try {
      await API.postSubscriberPreferences({
        jwt: token,
        subscribeTo,
        unsubscribeFrom,
      });
      setFormState(FORM_STATES.UPDATED);
      if (isValidRedirectURL(branding?.redirectURL)) {
        setCountingDown(true);
      }
    } catch {
      setErrorMessage('Failed to update preferences');
      /* Assume the endpoint call failed because of an invalid token */
      setTokenValid(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="subscribe__page client__specific d-flex flex-column">
      <PageHeader branding={branding as any} />
      <Checkbox
        className="all_campaigns"
        labelName={branding.selectAllNewslettersButtonText ?? 'All newsletters'}
        isSelected={campaignList.every(
          campaign => campaign.isSelected === true
        )}
        handleChange={handleAllSelect}
        id={branding.selectAllNewslettersButtonText ?? 'All newsletters'}
      />
      <CampaignList
        campaignList={campaignList}
        errorMessage={errorMessage ?? undefined}
        handleSelection={handleSelect}
      />
      {formState === FORM_STATES.PENDING && !success && (
        <UpdatePreferences
          branding={branding as any}
          handleCancel={handleCancel}
          handleUpdate={handleUpdate}
          isBusy={isBusy}
          isCancelDisabled={Boolean(isCancelDisabled.current)}
          isUpdateDisabled={Boolean(isUpdateDisabled)}
        />
      )}
      {formState === FORM_STATES.UPDATED && (
        <Row className="subscribe__footer manage__preferences__footer ml-3">
          <div className="subscribe__variable__width">
            <Tick />
            <span className="p-2">{branding.preferencesUpdatedCopy}</span>
          </div>
        </Row>
      )}
      {formState === FORM_STATES.CANCELLED && (
        <Row className="subscribe__footer manage__preferences__footer ml-3">
          <div className="subscribe__variable__width">
            <Tick />
            <span className="p-2">{branding.unsubscribedFromAllCopy}</span>
          </div>
        </Row>
      )}
      {success && (
        <>
          <Row className="subscribe__footer manage__preferences__footer ml-3">
            <div className="subscribe__variable__width">
              <Tick />
              <span className="p-2">{branding.preferencesUpdatedCopy}</span>
            </div>
            <div className="subscribe__variable__width">
              <Tick />
              <span className="p-2">{branding.unsubscribedFromAllCopy}</span>
            </div>
          </Row>
        </>
      )}
    </div>
  );
}

export default ManagePreferences;
